import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Flame, TrendingUp } from 'lucide-react';
const DURATION_MS = 30 * 60 * 1000; // 30 minutos
const STORAGE_KEY = 'mp_offer_deadline';
function getDeadline(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const ts = parseInt(stored, 10);
      if (!isNaN(ts) && ts > Date.now()) return ts;
    }
  } catch (e) {
    /* localStorage no disponible */
  }
  const deadline = Date.now() + DURATION_MS;
  try {
    localStorage.setItem(STORAGE_KEY, String(deadline));
  } catch (e) {
    /* noop */
  }
  return deadline;
}
function pad(n: number) {
  return String(n).padStart(2, '0');
}
export function UrgencyBanner() {
  const [remaining, setRemaining] = useState<number>(DURATION_MS);
  useEffect(() => {
    const deadline = getDeadline();
    const tick = () => {
      const diff = Math.max(0, deadline - Date.now());
      setRemaining(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  // La barra arranca en 70% y sube hacia ~95% a medida que corre el tiempo (cupos llenándose)
  const elapsedRatio = 1 - remaining / DURATION_MS;
  const progress = Math.min(95, 70 + elapsedRatio * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="max-w-2xl mx-auto rounded-2xl overflow-hidden border border-red-500/40 bg-slate-950 shadow-xl shadow-red-900/20" data-id="element-286">
      {/* Encabezado de alerta */}
      <div className="bg-red-600 px-4 py-2 flex items-center justify-center gap-2 text-center" data-id="element-287">
        <motion.span animate={{
        scale: [1, 1.25, 1]
      }} transition={{
        repeat: Infinity,
        duration: 1.2
      }} className="inline-block w-2.5 h-2.5 rounded-full bg-white" data-id="element-288" />
        <span className="text-white font-extrabold text-xs sm:text-sm tracking-wide uppercase flex items-center gap-1.5" data-id="element-289">
          <Flame className="w-4 h-4" data-id="element-290" /> Oferta por tiempo limitado
        </span>
      </div>

      <div className="p-4 sm:p-5" data-id="element-291">
        {/* Timer */}
        <div className="flex items-center justify-center gap-3 mb-3" data-id="element-292">
          <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400 shrink-0" data-id="element-293" />
          <div className="flex items-center gap-1.5" data-id="element-294">
            <TimeBox value={pad(minutes)} label="MIN" data-id="element-295" />
            <span className="text-amber-400 font-extrabold text-2xl sm:text-3xl -mt-3" data-id="element-296">
              :
            </span>
            <TimeBox value={pad(seconds)} label="SEG" data-id="element-297" />
          </div>
        </div>

        {/* Mensaje de urgencia */}
        <p className="text-center text-slate-200 text-xs sm:text-sm font-medium mb-3 leading-snug" data-id="element-298">
          Cuando se agoten los cupos de oferta, el precio{' '}
          <span className="text-red-400 font-extrabold" data-id="element-299">sube $12.000</span> y{' '}
          <span className="text-white font-bold" data-id="element-300">NO vuelve a bajar.</span>
        </p>

        {/* Barra de cupos */}
        <div className="space-y-1.5" data-id="element-301">
          <div className="flex items-center justify-between text-[10px] sm:text-xs font-bold" data-id="element-302">
            <span className="text-amber-400 flex items-center gap-1" data-id="element-303">
              <TrendingUp className="w-3 h-3" data-id="element-304" /> Cupos reclamados
            </span>
            <span className="text-red-400" data-id="element-305">{Math.round(progress)}%</span>
          </div>
          <div className="w-full h-2.5 sm:h-3 rounded-full bg-slate-800 overflow-hidden" data-id="element-306">
            <motion.div initial={{
            width: '70%'
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.8
          }} className="h-full rounded-full bg-gradient-to-r from-amber-500 to-red-500 relative" data-id="element-307">
              <span className="absolute inset-0 bg-white/20 animate-pulse" data-id="element-308" />
            </motion.div>
          </div>
          <p className="text-center text-[10px] sm:text-xs text-slate-400 font-medium" data-id="element-309">
            ¡Últimos cupos disponibles al precio actual!
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-310">
      <span className="bg-slate-800 border border-slate-700 text-white font-extrabold text-2xl sm:text-3xl tabular-nums rounded-lg px-2.5 sm:px-3 py-1 sm:py-1.5 min-w-[2.75rem] sm:min-w-[3.25rem] text-center" data-id="element-311">
        {value}
      </span>
      <span className="text-[9px] sm:text-[10px] text-slate-500 font-bold tracking-widest mt-1" data-id="element-312">
        {label}
      </span>
    </div>;
}